/**
 * META2-04 (PR #352): useAiConsent hook
 *
 * Gestiona el estado del consentimiento Ley 172-13 + helpers para grant/revoke.
 *
 * Usage:
 *   const { consent, isLoading, hasConsent, grant, revoke, refresh } = useAiConsent();
 *
 *   if (!hasConsent) return <AiConsentModal onAccept={() => grant(...)} />;
 */
import { useState, useEffect, useCallback } from 'react';
import { aiConsentApi, AiConsent, GrantConsentPayload } from '../api/ai-consent-api';

// Bump cuando política/términos cambien — forces re-consent
export const CURRENT_CONSENT_VERSION = '2026-06-03';

export interface UseAiConsentResult {
  consent: AiConsent | null;
  isLoading: boolean;
  error: Error | null;
  hasConsent: boolean;
  needsRefresh: boolean;
  grant: (payload?: Partial<GrantConsentPayload>) => Promise<AiConsent>;
  revoke: (reason?: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export interface UseAiConsentOptions {
  /**
   * GH#1875 — cuándo puede pedir el consentimiento a la API. El chatbot monta su
   * template SIEMPRE (aunque esté cerrado) y este hook pegaba a /ai/consent en cada
   * página, con el chat cerrado. Con `enabled: isOpen` la llamada solo sale al abrir.
   * Por defecto `true` para no cambiar el comportamiento de los demás consumidores.
   */
  enabled?: boolean;
}

export function useAiConsent(options?: UseAiConsentOptions): UseAiConsentResult {
  const enabled = options?.enabled ?? true;
  const [consent, setConsent] = useState<AiConsent | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const active = await aiConsentApi.getActive();
      setConsent(active);
    } catch (err) {
      setError(err as Error);
      setConsent(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    refresh();
  }, [enabled, refresh]);

  const grant = useCallback(
    async (payload?: Partial<GrantConsentPayload>): Promise<AiConsent> => {
      const fullPayload: GrantConsentPayload = {
        consentVersion: payload?.consentVersion ?? CURRENT_CONSENT_VERSION,
        dataCategories: payload?.dataCategories,
        providers: payload?.providers,
      };
      const granted = await aiConsentApi.grant(fullPayload);
      setConsent(granted);
      return granted;
    },
    [],
  );

  const revoke = useCallback(async (reason?: string) => {
    await aiConsentApi.revoke(reason);
    setConsent(null);
  }, []);

  // hasConsent = active and matches current version (else re-consent needed)
  const hasConsent = !!consent && !consent.revokedAt;
  const needsRefresh =
    !!consent &&
    !consent.revokedAt &&
    consent.consentVersion !== CURRENT_CONSENT_VERSION;

  return {
    consent,
    isLoading,
    error,
    hasConsent,
    needsRefresh,
    grant,
    revoke,
    refresh,
  };
}
